import { useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { useSEO, generateLocalBusinessSchema, generateServiceSchema, generateBreadcrumbSchema } from '../../utils/seo';

const NOTARY_SERVICES = [
  {
    service: 'Vehicle Title Notarization',
    icon: 'ri-car-line',
    desc: 'Specialized notary services for all vehicle title transactions.',
    docs: ['Title transfers', 'Bill of sale', 'Lien releases', 'Gift affidavits', 'Odometer statements'],
    badge: 'Most Requested',
    badgeColor: 'bg-[#F5A623] text-white',
  },
  {
    service: 'Document Notarization',
    icon: 'ri-file-text-line',
    desc: 'Official notarization of all legal documents.',
    docs: ['Affidavits', 'Power of Attorney', 'Contracts', 'Deeds', 'Wills & trusts'],
    badge: 'General',
    badgeColor: 'bg-[#003366] text-white',
  },
  {
    service: 'Real Estate Documents',
    icon: 'ri-home-line',
    desc: 'Notarization for real estate transactions and property transfers.',
    docs: ['Property deeds', 'Mortgage documents', 'Lease agreements', 'Property transfers', 'Refinancing papers'],
    badge: 'Real Estate',
    badgeColor: 'bg-green-600 text-white',
  },
  {
    service: 'Business Documents',
    icon: 'ri-building-line',
    desc: 'Corporate and business document notarization.',
    docs: ['Articles of incorporation', 'Partnership agreements', 'Business contracts', 'Corporate resolutions', 'Operating agreements'],
    badge: 'Business',
    badgeColor: 'bg-slate-600 text-white',
  },
];

const SPECIAL_SERVICES = [
  { icon: 'ri-car-line', title: 'Mobile Notary', desc: 'We come to your location — home, office, or hospital. Convenient and flexible.' },
  { icon: 'ri-time-line', title: 'After Hours', desc: 'Evening and weekend appointments available for urgent notarization needs.' },
  { icon: 'ri-file-list-line', title: 'Bulk Documents', desc: 'Multiple document notarization with volume discounts for businesses.' },
  { icon: 'ri-bank-line', title: 'Loan Signings', desc: 'Certified signing agent for mortgage and loan document signings.' },
  { icon: 'ri-global-line', title: 'Apostille Services', desc: 'International document authentication for use abroad.' },
  { icon: 'ri-file-copy-line', title: 'Copy Certification', desc: 'Certified true copies of original documents — passports, diplomas, certificates.' },
];

const STEPS = [
  { step: '01', icon: 'ri-search-line', title: 'Document Review', desc: 'We review your documents for completeness and identify all required signers.' },
  { step: '02', icon: 'ri-shield-check-line', title: 'Identity Verification', desc: 'Verify identity of all signers with valid government-issued photo ID.' },
  { step: '03', icon: 'ri-eye-line', title: 'Witness Signing', desc: 'Observe the document signing to ensure understanding and willingness.' },
  { step: '04', icon: 'ri-award-line', title: 'Official Notarization', desc: 'Apply official notary seal, sign certificate, and record in journal.' },
];

export default function NotaryServicesPage() {
  const [openService, setOpenService] = useState<number | null>(0);
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://speedydmvservices.com';

  useSEO({
    title: 'Notary Services Las Vegas Nevada - Certified Notary Public | Speedy DMV',
    description: 'Professional notary services in Las Vegas Nevada. Vehicle titles, affidavits, powers of attorney, real estate, and business documents notarized. Licensed notary public. Call 702.979.7202.',
    keywords: 'notary services Las Vegas, notary public Nevada, document notarization Las Vegas, mobile notary Las Vegas NV, vehicle title notary Nevada',
    canonical: `${siteUrl}/notary-services`,
    schema: {
      "@context": "https://schema.org",
      "@graph": [
        generateLocalBusinessSchema(),
        generateServiceSchema('Notary Public Services', 'Licensed notary public services in Las Vegas Nevada for vehicle titles, affidavits, powers of attorney, and all document notarization needs.'),
        generateBreadcrumbSchema([{ name: 'Home', url: '/' }, { name: 'Services', url: '/services' }, { name: 'Notary Services', url: '/notary-services' }])
      ]
    }
  });

  return (
    <div className="min-h-screen bg-white font-sans">
      <Header />

      {/* HERO */}
      <section className="relative pt-[104px]">
        <div className="relative h-[400px] md:h-[460px] overflow-hidden">
          <img
            src="https://public.readdy.ai/ai/img_res/1a43183a13ad4ab22f9379a0daf47479.jpg"
            alt="Notary Services Las Vegas"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#003366]/80"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <div className="flex items-center space-x-2 text-white/60 text-xs mb-5">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/services" className="hover:text-white transition-colors">Services</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-[#F5A623]">Notary Services</span>
            </div>
            <div className="inline-flex items-center space-x-2 bg-[#F5A623] text-white text-xs font-bold px-4 py-1.5 rounded mb-4 uppercase tracking-widest">
              <i className="ri-shield-check-line"></i>
              <span>Certified Notary Public — Las Vegas, Nevada</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white leading-tight mb-4 max-w-3xl">
              Notary Services <span className="text-[#F5A623]">Las Vegas</span>
            </h1>
            <p className="text-white/85 text-lg max-w-2xl mb-7">
              Professional notarization for vehicle titles, legal documents, real estate, and business papers. Fast, certified, and reliable.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-7 py-3.5 font-bold text-sm transition-colors whitespace-nowrap rounded">
                <i className="fas fa-phone text-xs"></i>
                <span>Call Now: 702.979.7202</span>
              </a>
              <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-7 py-3.5 font-bold text-sm transition-colors whitespace-nowrap rounded">
                <i className="ri-calendar-check-line"></i>
                <span>Book Appointment</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NOTICE BAR */}
      <div className="bg-[#FFF8E7] border-b-2 border-[#F5A623]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div className="flex items-center space-x-3">
            <i className="ri-information-line text-[#F5A623] text-lg flex-shrink-0"></i>
            <p className="text-sm text-[#7a5c00]">
              <strong>We are NOT the Nevada DMV.</strong> Speedy DMV Services is a licensed, independent 3rd party provider. We assist with DMV transactions for a service fee. Official DMV: <a href="https://dmv.nv.gov" target="_blank" rel="noopener noreferrer nofollow" className="underline font-semibold">dmv.nv.gov</a>
            </p>
          </div>
        </div>
      </div>

      {/* QUICK STATS */}
      <div className="bg-[#003366]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#1a4480]">
            {[
              { value: '$120–$150 + DMV', label: 'Service Fee' },
              { value: 'Same Day', label: 'Service Available' },
              { value: 'Mobile', label: 'We Come to You' },
              { value: 'All Documents', label: 'Types Handled' },
            ].map((s, i) => (
              <div key={i} className="py-4 px-6 text-center">
                <p className="text-[#F5A623] text-lg font-bold">{s.value}</p>
                <p className="text-white/60 text-xs mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* HOW IT WORKS */}
      <section className="py-14 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Our Notarization Process</h2>
          <p className="text-gray-500 mb-10 pl-5">Four simple steps — professional, accurate, and legally compliant.</p>
          <div className="grid md:grid-cols-4 gap-6 relative">
            <div className="hidden md:block absolute top-10 left-[12.5%] right-[12.5%] h-0.5 bg-[#003366]/15"></div>
            {STEPS.map((item, i) => (
              <div key={i} className="text-center relative group">
                <div className="w-20 h-20 mx-auto flex items-center justify-center bg-[#003366] rounded-full mb-4 relative z-10 group-hover:bg-[#F5A623] transition-colors">
                  <i className={`${item.icon} text-[#F5A623] group-hover:text-white text-2xl transition-colors`}></i>
                </div>
                <span className="text-[#F5A623] text-xs font-bold tracking-widest uppercase">Step {item.step}</span>
                <h3 className="font-bold text-[#003366] text-base mt-1 mb-2">{item.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SERVICES ACCORDION */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Notary Services We Offer</h2>
          <p className="text-gray-500 mb-8 pl-5">Select a service type to see common documents and details.</p>
          <div className="grid md:grid-cols-2 gap-5">
            {NOTARY_SERVICES.map((t, i) => (
              <div
                key={i}
                className={`border rounded-lg overflow-hidden transition-all cursor-pointer ${openService === i ? 'border-[#003366] shadow-md' : 'border-gray-200 hover:border-[#003366]/40'}`}
                onClick={() => setOpenService(openService === i ? null : i)}
              >
                <div className={`flex items-center justify-between px-5 py-4 ${openService === i ? 'bg-[#003366]' : 'bg-white hover:bg-gray-50'} transition-colors`}>
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 flex items-center justify-center rounded flex-shrink-0 ${openService === i ? 'bg-[#F5A623]' : 'bg-[#003366]'}`}>
                      <i className={`${t.icon} ${openService === i ? 'text-white' : 'text-[#F5A623]'} text-base`}></i>
                    </div>
                    <div>
                      <div className="flex items-center space-x-2">
                        <p className={`font-bold text-sm ${openService === i ? 'text-white' : 'text-[#003366]'}`}>{t.service}</p>
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${t.badgeColor}`}>{t.badge}</span>
                      </div>
                      <p className={`text-xs ${openService === i ? 'text-white/70' : 'text-gray-400'}`}>{t.desc}</p>
                    </div>
                  </div>
                  <i className={`${openService === i ? 'ri-arrow-up-s-line text-white' : 'ri-arrow-down-s-line text-[#003366]'} text-xl flex-shrink-0`}></i>
                </div>
                {openService === i && (
                  <div className="px-5 py-4 bg-white border-t border-gray-100">
                    <p className="text-xs font-bold text-[#003366] uppercase tracking-widest mb-3">Common Documents</p>
                    <ul className="space-y-2">
                      {t.docs.map((doc, j) => (
                        <li key={j} className="flex items-center space-x-2 text-sm text-gray-700">
                          <i className="ri-checkbox-circle-fill text-[#F5A623] flex-shrink-0"></i>
                          <span>{doc}</span>
                        </li>
                      ))}
                    </ul>
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <a href="tel:7029797202" className="inline-flex items-center space-x-2 bg-[#003366] hover:bg-[#002244] text-white px-5 py-2.5 rounded font-bold text-sm transition-colors whitespace-nowrap">
                        <i className="fas fa-phone text-xs"></i>
                        <span>Call to Schedule</span>
                      </a>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SPECIALIZED SERVICES */}
      <section className="py-14 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Specialized Notary Services</h2>
          <p className="text-gray-500 mb-10 pl-5">Beyond standard notarization — we offer a full range of specialized services.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {SPECIAL_SERVICES.map((s, i) => (
              <div key={i} className="group flex items-start space-x-4 p-5 border border-gray-200 rounded-lg hover:border-[#003366] hover:bg-blue-50/30 transition-all">
                <div className="w-11 h-11 flex items-center justify-center bg-[#003366] rounded flex-shrink-0 group-hover:bg-[#F5A623] transition-colors">
                  <i className={`${s.icon} text-[#F5A623] group-hover:text-white text-base transition-colors`}></i>
                </div>
                <div>
                  <h3 className="font-bold text-[#003366] text-sm mb-1">{s.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ID REQUIREMENTS */}
      <section className="py-14 bg-gray-50 border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#003366] mb-2 border-l-4 border-[#F5A623] pl-4">Identification Requirements</h2>
          <p className="text-gray-500 mb-10 pl-5">All signers must present valid identification at the time of notarization.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: 'Primary ID',
                icon: 'ri-id-card-line',
                color: 'bg-[#003366]',
                items: ["Driver's License", 'State ID Card', 'Passport', 'Military ID'],
              },
              {
                title: 'Secondary ID',
                icon: 'ri-file-list-line',
                color: 'bg-green-700',
                items: ['Social Security Card', 'Birth Certificate', 'Credit Card', 'Employee ID'],
              },
              {
                title: 'Special Cases',
                icon: 'ri-information-line',
                color: 'bg-[#F5A623]',
                items: ['Credible witness', 'Personal knowledge', 'Oath of identity', 'Consular ID'],
              },
            ].map((col, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <div className={`${col.color} px-5 py-3 flex items-center space-x-2`}>
                  <i className={`${col.icon} text-white text-base`}></i>
                  <span className="text-white font-bold text-sm">{col.title}</span>
                </div>
                <ul className="p-5 space-y-2.5">
                  {col.items.map((item, j) => (
                    <li key={j} className="flex items-start space-x-2 text-sm text-gray-700">
                      <i className="ri-arrow-right-s-line text-[#F5A623] flex-shrink-0 mt-0.5"></i>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEES */}
      <section className="py-14 bg-white border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white border border-gray-200 rounded-lg overflow-hidden max-w-2xl mx-auto">
            <div className="bg-[#003366] px-6 py-4">
              <h3 className="text-white font-bold text-base flex items-center space-x-2">
                <i className="ri-money-dollar-circle-line text-[#F5A623]"></i>
                <span>Notary Service Fees</span>
              </h3>
            </div>
            <div className="p-6 space-y-3">
              {[
                { label: 'Standard Notarization (per signature)', value: 'Call for Quote' },
                { label: 'Mobile Notary (travel fee)', value: 'Additional fee' },
                { label: 'After Hours / Weekend', value: 'Premium rate' },
                { label: 'Loan Signing Package', value: 'Call for quote' },
                { label: 'Bulk Document Package', value: 'Volume discount' },
              ].map((row, i) => (
                <div key={i} className={`flex justify-between items-center py-2.5 ${i < 4 ? 'border-b border-gray-100' : ''}`}>
                  <span className="text-gray-600 text-sm">{row.label}</span>
                  <span className="font-bold text-[#003366] text-sm">{row.value}</span>
                </div>
              ))}
              <div className="mt-4 bg-[#FFF8E7] border border-[#F5A623]/30 rounded p-3">
                <p className="text-xs text-[#7a5c00]"><strong>Note:</strong> Nevada state law governs maximum notary fees. Call us for exact pricing based on your document needs.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* LEGAL DISCLAIMER */}
      <section className="py-8 bg-[#FFF8E7] border-t border-[#F5A623]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-start space-x-3">
            <i className="ri-information-line text-[#F5A623] text-lg flex-shrink-0 mt-0.5"></i>
            <p className="text-sm text-[#7a5c00] font-medium leading-relaxed">
              I AM NOT AN ATTORNEY IN THE STATE OF NEVADA. I AM NOT AUTHORIZED TO GIVE LEGAL ADVICE OR LEGAL REPRESENTATION. I MAY NOT ACCEPT FEES FOR GIVING LEGAL ADVICE OR LEGAL REPRESENTATION.
            </p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-[#003366]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-3">
            Need Professional <span className="text-[#F5A623]">Notary Services?</span>
          </h2>
          <p className="text-white/75 mb-8 leading-relaxed">
            Trust our certified notary professionals for all your document authentication needs — fast, accurate, and legally compliant.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
            <a href="tel:7029797202" className="flex items-center justify-center space-x-2 bg-[#F5A623] hover:bg-[#e09510] text-white px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="fas fa-phone text-sm"></i>
              <span>Call 702.979.7202</span>
            </a>
            <Link to="/appointment" className="flex items-center justify-center space-x-2 bg-white hover:bg-gray-100 text-[#003366] px-8 py-4 font-bold text-base transition-colors whitespace-nowrap rounded">
              <i className="ri-calendar-check-line"></i>
              <span>Book Appointment</span>
            </Link>
          </div>
          <div className="flex flex-wrap justify-center gap-5">
            {['Certified Notary Public', 'Mobile Service Available', 'All Document Types', 'Same-Day Service'].map(b => (
              <div key={b} className="flex items-center space-x-1.5 text-white/60 text-xs">
                <i className="ri-checkbox-circle-fill text-[#F5A623]"></i>
                <span>{b}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
